'use client'

import {
  useParams as useNextParams,
  usePathname,
  useRouter,
  useSearchParams as useNextSearchParams,
} from 'next/navigation'

type SearchParamsInit = URLSearchParams | Record<string, string> | ((previous: URLSearchParams) => URLSearchParams)

export function useNavigate(): (to: string | number) => void {
  const router = useRouter()
  return (to) => {
    if (typeof to === 'number') {
      if (to < 0) {
        router.back()
      }
      return
    }
    router.push(to)
  }
}

export function useParams<T extends Record<string, string> = Record<string, string>>(): T {
  const params = useNextParams()
  return params as T
}

export function useLocation(): { pathname: string } {
  return { pathname: usePathname() }
}

export function useSearchParams(): [
  URLSearchParams,
  (next: SearchParamsInit, options?: { replace?: boolean }) => void,
] {
  const router = useRouter()
  const pathname = usePathname()
  const readonlyParams = useNextSearchParams()
  const current = new URLSearchParams(readonlyParams.toString())

  function setSearchParams(next: SearchParamsInit, options?: { replace?: boolean }): void {
    let params: URLSearchParams
    if (typeof next === 'function') {
      params = next(new URLSearchParams(readonlyParams.toString()))
    } else if (next instanceof URLSearchParams) {
      params = next
    } else {
      params = new URLSearchParams(next)
    }

    const query = params.toString()
    const href = query ? `${pathname}?${query}` : pathname
    if (options?.replace) {
      router.replace(href)
      return
    }
    router.push(href)
  }

  return [current, setSearchParams]
}
